import * as React from 'react';
import {
  Avatar,
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Stack,
  Tooltip,
  alpha,
} from '@mui/material';
import PanToolAltRoundedIcon from '@mui/icons-material/PanToolAltRounded';
import KeyboardTabRoundedIcon from '@mui/icons-material/KeyboardTabRounded';
import ExpandRoundedIcon from '@mui/icons-material/ExpandRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

const TOOL_CONFIG = {
  'baseline-point': {
    label: 'Baseline markør',
    hint: 'Klik for at droppe markør',
    palette: 'info',
    icon: <KeyboardTabRoundedIcon color="info" />,
  },
  'baseline-range': {
    label: 'Baselineperiode',
    hint: 'Klik og træk for at vælge',
    palette: 'info',
    icon: (
      <ExpandRoundedIcon sx={{ transform: 'rotate(90deg)' }} color="info" />
    ),
  },
  'comparison-point': {
    label: 'Sammenligningsmarkør',
    hint: 'Klik for at droppe markør',
    palette: 'success',
    icon: <KeyboardTabRoundedIcon color="success" />,
  },
  'comparison-range': {
    label: 'Sammenligningsperiode',
    hint: 'Klik og træk for at vælge',
    palette: 'success',
    icon: (
      <ExpandRoundedIcon sx={{ transform: 'rotate(90deg)' }} color="success" />
    ),
  },
};

function ToolAvatar({ tool, size = 36 }) {
  if (!tool) {
    return (
      <Avatar
        sx={{
          width: size,
          height: size,
          borderRadius: 2,
          bgcolor: (t) => alpha(t.palette.divider, 0.1),
        }}
      >
        <PanToolAltRoundedIcon fontSize="small" sx={{ color: 'gray' }} />
      </Avatar>
    );
  }
  const cfg = TOOL_CONFIG[tool];
  return (
    <Avatar
      sx={{
        width: size,
        height: size,
        borderRadius: 2,
        backgroundColor: (t) => alpha(t.palette[cfg.palette].main, 0.1),
      }}
    >
      {cfg.icon}
    </Avatar>
  );
}

export default function ToolMenu({
  activeTool,
  activeStep,
  setActiveTool,
  disabled,
}) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const menuId = 'tool-menu';

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const selectTool = (tool) => {
    setActiveTool((t) => (t === tool ? null : tool));
    handleClose();
  };

  const renderToolItem = (tool) => {
    const selected = activeTool === tool;
    if (!tool) {
      // "None" option
      return (
        <MenuItem
          key="none"
          role="menuitemradio"
          aria-checked={selected}
          selected={selected}
          onClick={() => selectTool(null)}
          sx={{ m: 1, borderRadius: 2 }}
        >
          <ListItemIcon>
            <ToolAvatar tool={null} />
          </ListItemIcon>
          <ListItemText
            sx={{ ml: 2 }}
            primary="Ingen værktøj valgt"
            secondary="Kun panorering og zoom"
          />
        </MenuItem>
      );
    }
    const cfg = TOOL_CONFIG[tool];
    return (
      <MenuItem
        key={tool}
        role="menuitemradio"
        aria-checked={selected}
        selected={selected}
        onClick={() => selectTool(tool)}
        sx={{ m: 1, borderRadius: 2 }}
      >
        <ListItemIcon>
          <Avatar
            sx={{
              mr: 2,
              borderRadius: 3,
              backgroundColor: (t) => alpha(t.palette[cfg.palette].main, 0.1),
            }}
          >
            {cfg.icon}
          </Avatar>
        </ListItemIcon>
        <ListItemText primary={cfg.label} secondary={cfg.hint} />
      </MenuItem>
    );
  };

  return (
    <>
      {activeStep === 2 && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          {renderToolItem('baseline-point')}
          {renderToolItem('baseline-range')}
          <Divider sx={{ my: 0.5 }} />
          {renderToolItem(null)}
        </Stack>
      )}
      {activeStep === 3 && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          {renderToolItem('comparison-point')}
          {renderToolItem('comparison-range')}
          <Divider sx={{ my: 0.5 }} />
          {renderToolItem(null)}
        </Stack>
      )}
    </>
  );
}
